'use client'

import {
    AdEndpointRequestOptions,
    AdResponse,
    fetchAdFromEndpoint,
    hasAnyAd,
    mergeResponses,
} from '@/app/util/ads-endpoint'
import { Banner } from '@/app/util/banner'
import { giphyUrl } from '@/app/util/env'
import HTLScript from '@/app/util/htl-script'
import { getIdFromSlug } from '@/util/url'
import { getFirstPathname, getPageOptionsBasedOnPath } from 'analytics/src/utils/page'
import { usePathname, useSearchParams } from 'next/navigation'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import AdsContext, { AdsContextProps } from './ads'

type Props = {
    children: ReactNode
    banner?: Banner
    htlAds?: string[]
    pageName?: string
    // could be any ad - used as a flag for the search bar
    hasTopLeaderboardAd?: boolean
    adResponse?: AdResponse
    adLog?: string
    geoTargetedRequest?: AdEndpointRequestOptions
}

function useHtlAds(adUnits: string[]) {
    const isAdUnitEnabled = (unitName: string) => adUnits.includes(unitName)
    const pathname = usePathname()
    useEffect(() => {
        if (adUnits.length > 0) {
            const { pageCategory } = getPageOptionsBasedOnPath(getFirstPathname(pathname))
            const id = pageCategory === 'gif' || pageCategory === 'sticker' ? getIdFromSlug(pathname) : ''
            const htlbid = (window.htlbid = window.htlbid || ({} as HTLBid))
            htlbid.cmd = htlbid.cmd || []
            window.didomiOnReady.push(() => {
                htlbid.cmd.push(() => {
                    const isTesting = giphyUrl === 'https://giphy.com' ? 'no' : 'yes'
                    console.log(`htlbid pageCategory`, pageCategory, 'testing:', isTesting, 'id', id)
                    htlbid.setTargeting('is_testing', isTesting) // Set to "no" for production
                    htlbid.setTargeting('category', pageCategory)
                    htlbid.setTargeting('post_id', id)
                    htlbid.layout()
                })
            })
        }
    }, [adUnits, pathname])
    return isAdUnitEnabled
}

const useAdManager = (adServerResponse?: AdResponse, geoTargetedRequest?: AdEndpointRequestOptions) => {
    const isAnyAdEnabled = () => hasAnyAd(adServerResponse)
    const pathname = usePathname()
    const searchParams = useSearchParams()

    const searchParamString = useRef(searchParams.toString())
    const isFetching = useRef('')

    // we refetch ad data on the client for targeting
    const [adClientData, setAdClientData] = useState<AdResponse | undefined>()

    useEffect(() => {
        if (
            adServerResponse &&
            geoTargetedRequest &&
            (isFetching.current !== pathname ||
                (searchParamString.current !== searchParams.toString() && pathname === '/')) // handles change in search type on the homepage
        ) {
            isFetching.current = pathname
            searchParamString.current = searchParams.toString()
            const doFetch = async () => {
                let [geotargetGridAndBannerAdsResponse, error] = await fetchAdFromEndpoint(geoTargetedRequest)
                if (error) {
                    console.error(`Error fetching client ad data`, error)
                }
                // the order of args here prioritizes the server data, ensuring that the correct companion gif is used in the grid
                setAdClientData(mergeResponses(geotargetGridAndBannerAdsResponse, adServerResponse))
            }
            const viewKeys = Object.keys(adServerResponse.decisions || [])
            if (
                viewKeys.some((view: string) => {
                    return !!adServerResponse.decisions?.[view]
                })
            ) {
                doFetch()
            }
        }
    }, [geoTargetedRequest, adServerResponse, pathname, searchParams])
    return { adClientData, isAnyAdEnabled }
}

const AdsContextManager = ({
    children,
    htlAds = [],
    pageName,
    adResponse: adServerData,
    hasTopLeaderboardAd,
    geoTargetedRequest,
    adLog,
    banner,
}: Props) => {
    useEffect(() => {
        if (adServerData) console.log(`adServerData`, adServerData)
        if (adLog) console.log(`adLog`, adLog)
        if (geoTargetedRequest) console.log(`geoTargetedRequest`, geoTargetedRequest)
    }, [adLog, adServerData, geoTargetedRequest])
    const isHTLAdEnabled = useHtlAds(htlAds)
    const { adClientData, isAnyAdEnabled } = useAdManager(adServerData, geoTargetedRequest)
    const value: AdsContextProps = useMemo(
        () => ({
            htlAds,
            isHTLAdEnabled,
            pageName,
            adClientData,
            banner,
            isAnyAdEnabled,
            hasTopLeaderboardAd,
            adServerData,
        }),
        [htlAds, banner, isHTLAdEnabled, isAnyAdEnabled, adClientData, pageName, adServerData, hasTopLeaderboardAd]
    ) as AdsContextProps
    return (
        <AdsContext.Provider value={value}>
            {htlAds.length > 0 && <HTLScript />}
            {children}
        </AdsContext.Provider>
    )
}

export default AdsContextManager
